import { useState, useCallback } from 'react';
import { api } from '@/api/client';
import { SavedOutput } from '@/api/types';
import { handleAppError } from '@/lib/errorUtils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export function useSavedOutputs(workspaceId: string) {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['savedOutputs', workspaceId],
    queryFn: async () => {
      const response = await api.getSavedOutputs(workspaceId);
      return response.items;
    },
  });

  const outputs = data || [];
  const error = queryError ? handleAppError(queryError, 'Failed to fetch saved outputs.') : null;

  const saveMutation = useMutation({
    mutationFn: async (payload: Omit<SavedOutput, 'id' | 'created'>) => {
      const { output } = await api.saveOutput(workspaceId, payload);
      return output;
    },
    onSuccess: (newOutput) => {
      queryClient.setQueryData(['savedOutputs', workspaceId], (oldData: SavedOutput[] | undefined) => {
        return oldData ? [newOutput, ...oldData] : [newOutput];
      });
    },
    onError: (err) => {
      const errorMsg = handleAppError(err, 'Failed to save output.');
      toast.error(errorMsg);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<SavedOutput> }) => {
      return await api.updateSavedOutput(workspaceId, id, updates);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['savedOutputs', workspaceId], (oldData: SavedOutput[] | undefined) => {
        if (!oldData) return [updated];
        const without = oldData.filter(o => o.id !== updated.id);
        return [updated, ...without];
      });
    },
    onError: (err) => {
      handleAppError(err, 'Failed to update output.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.deleteSavedOutput(workspaceId, id);
      return id;
    },
    onSuccess: (id) => {
      queryClient.setQueryData(['savedOutputs', workspaceId], (oldData: SavedOutput[] | undefined) => {
        return oldData ? oldData.filter((o) => o.id !== id) : [];
      });
    },
    onError: (err) => {
      handleAppError(err, 'Failed to delete output.');
    }
  });

  const saveOutput = useCallback((payload: Omit<SavedOutput, 'id' | 'created'>) => {
    return saveMutation.mutateAsync(payload);
  }, [saveMutation]);

  const updateOutput = useCallback((id: string, updates: Partial<SavedOutput>) => {
    return updateMutation.mutateAsync({ id, updates });
  }, [updateMutation]);

  const deleteOutput = useCallback((id: string) => {
    return deleteMutation.mutateAsync(id);
  }, [deleteMutation]);

  return {
    outputs,
    loading,
    error,
    saveOutput,
    updateOutput,
    deleteOutput,
    refresh: refetch,
  };
}
